import { FormEvent, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Sporsmal, SvarInn, Svarverdi } from '../../api/reise';
import Filfelt, { filFraSvar, type ValgtFil } from './felt/Filfelt';
import Policyfelt, { policyFraSvar, policyTilSvar, type Policy } from './felt/Policyfelt';
import Profilvelger from './felt/Profilvelger';
import Tabellfelt, { nyRad, raderFraSvar, raderTilSvar, type Rad } from './felt/Tabellfelt';

/** Typene som vises. */
export const STOTTEDE_TYPER = ['ett_valg', 'flere_valg', 'sjekkliste', 'tall', 'tekst', 'tabell', 'policy', 'scenario', 'fil'];

const ANNET = 'annet';

interface Props {
  sporsmal: Sporsmal;
  lagrer: boolean;
  /** Feilkoden fra API-et, som oversettes under `feil.`. */
  feil: string | null;
  kanGaTilbake: boolean;
  /** Lagrer svaret. Gir `true` når det er lagret, så utkastet kan slettes. */
  onSvar: (svar: SvarInn) => Promise<boolean>;
  onTilbake: () => void;
  /** Arbeidsbordet: en ansatt retter svaret. Da vises ikke «Vet ikke» og «Hopp over», og knappen heter noe annet. */
  konsulent?: { lagreTekst: string };
}

/**
 * Det kunden har fylt ut, men ikke lagret. Det lagres på enheten til kunden går videre, så ingenting går
 * tapt hvis siden lukkes.
 */
interface Utkast {
  tekst?: string;
  annet?: string;
  valgte?: string[];
  rader?: Record<string, string | boolean>[];
  policy?: Policy;
}

const utkastNokkel = (s: Sporsmal) => `onboard.utkast.${s.id}.${s.instans}`;

function lesUtkast(s: Sporsmal): Utkast | null {
  try {
    const lagret = localStorage.getItem(utkastNokkel(s));
    return lagret ? JSON.parse(lagret) : null;
  } catch {
    return null;
  }
}

function skrivUtkast(s: Sporsmal, utkast: Utkast | null) {
  try {
    if (utkast) localStorage.setItem(utkastNokkel(s), JSON.stringify(utkast));
    else localStorage.removeItem(utkastNokkel(s));
  } catch {
    // Privat modus eller blokkert lagring: utkastet lagres ikke
  }
}

/**
 * Startverdien: svaret som er gitt. Ingenting er forhåndsvalgt. Kunden får anbefalingen bare ved å be om den
 * («Vet ikke, hva anbefaler dere?»), se docs/beslutninger/0039-uttrykk-og-anbefaling.md.
 */
function startvalg(s: Sporsmal): string[] {
  const verdi = s.svar?.verdi;
  if (typeof verdi === 'string') return [verdi];
  if (Array.isArray(verdi)) return (verdi as unknown[]).filter((v): v is string => typeof v === 'string');
  return [];
}

function starttekst(s: Sporsmal): string {
  const verdi = s.svar?.verdi;
  return typeof verdi === 'string' || typeof verdi === 'number' ? String(verdi) : '';
}

/** Radene: utkastet, ellers svaret som er gitt, ellers én tom rad å begynne på. */
function startrader(s: Sporsmal, utkast: Utkast | null): Rad[] {
  if (utkast?.rader) return utkast.rader.map((celler) => nyRad(celler));
  const fraSvar = raderFraSvar(s.svar?.verdi);
  return s.svar ? fraSvar : [nyRad()];
}

/**
 * Ett spørsmål: alternativene (eller feltene), «Annet», «Vet ikke», «Hvorfor spør vi?», og knappene for å gå
 * tilbake og videre. Overskriften får fokus når et nytt spørsmål vises, så tastatur og skjermleser følger med.
 */
export default function SporsmalSkjema({ sporsmal, lagrer, feil, kanGaTilbake, onSvar, onTilbake, konsulent }: Props) {
  const { t } = useTranslation();
  const overskrift = useRef<HTMLHeadingElement>(null);
  const [utkast] = useState(() => lesUtkast(sporsmal));

  const [valgte, setValgte] = useState<string[]>(() => utkast?.valgte ?? startvalg(sporsmal));
  const [tekst, setTekst] = useState(() => utkast?.tekst ?? starttekst(sporsmal));
  const [annet, setAnnet] = useState(() => utkast?.annet ?? sporsmal.svar?.kundensOrd ?? '');
  const [rader, setRader] = useState<Rad[]>(() => startrader(sporsmal, utkast));
  const [policy, setPolicy] = useState<Policy>(() => utkast?.policy ?? policyFraSvar(sporsmal.svar?.verdi));
  const [fil, setFil] = useState<ValgtFil | null>(() => filFraSvar(sporsmal.svar?.verdi));
  const [endret, setEndret] = useState(false);
  const [kontroll, setKontroll] = useState<{ nokkel: string; verdier?: Record<string, unknown> } | null>(null);

  const typen = sporsmal.type;
  const stottet = STOTTEDE_TYPER.includes(typen);
  const skjemaId = `sporsmal-${sporsmal.id}`;

  useEffect(() => {
    overskrift.current?.focus();
  }, [sporsmal.id, sporsmal.instans]);

  // Utkastet skrives når kunden endrer noe
  useEffect(() => {
    if (!endret) return;
    skrivUtkast(sporsmal, {
      tekst: typen === 'tekst' || typen === 'tall' ? tekst : undefined,
      annet: valgte.includes(ANNET) ? annet : undefined,
      valgte: ['flere_valg', 'sjekkliste', 'scenario'].includes(typen) ? valgte : undefined,
      rader: typen === 'tabell' ? rader.map((r) => r.celler) : undefined,
      policy: typen === 'policy' ? policy : undefined,
    });
  }, [endret, sporsmal, typen, tekst, annet, valgte, rader, policy]);

  /** Alle endringer går hit: feilmeldingen fjernes, og utkastet lagres. */
  function endre<T>(sett: (verdi: T) => void) {
    return (verdi: T) => {
      setKontroll(null);
      setEndret(true);
      sett(verdi);
    };
  }

  function velg(verdi: string) {
    endre(setValgte)(typen === 'ett_valg' ? [verdi] : valgte.includes(verdi) ? valgte.filter((v) => v !== verdi) : [...valgte, verdi]);
  }

  /** Kontrollerer svaret her først, så kunden får beskjed med en gang. API-et kontrollerer det uansett. */
  function lagSvar(): { verdi: Svarverdi; kundensOrd: string | null } | { nokkel: string; verdier?: Record<string, unknown> } {
    const egneOrd = valgte.includes(ANNET) ? annet.trim() : '';
    switch (typen) {
      case 'ett_valg':
        if (valgte.length === 0) return { nokkel: 'reise.kontroll.velgEtt' };
        if (valgte[0] === ANNET && !egneOrd) return { nokkel: 'reise.kontroll.beskrivAnnet' };
        return { verdi: valgte[0], kundensOrd: egneOrd || null };
      case 'flere_valg':
      case 'sjekkliste':
        // En sjekkliste kan være tom: ingen av punktene gjelder
        if (typen === 'flere_valg' && valgte.length === 0) return { nokkel: 'reise.kontroll.velgMinstEtt' };
        if (valgte.includes(ANNET) && !egneOrd) return { nokkel: 'reise.kontroll.beskrivAnnet' };
        return { verdi: valgte, kundensOrd: egneOrd || null };
      case 'scenario':
        if (valgte.length === 0) return { nokkel: 'reise.kontroll.velgProfil' };
        return { verdi: valgte, kundensOrd: null };
      case 'tall': {
        const tall = Number(tekst.trim().replace(',', '.'));
        if (tekst.trim() === '' || Number.isNaN(tall)) return { nokkel: 'reise.kontroll.skrivTall' };
        if (sporsmal.min !== null && tall < sporsmal.min) return { nokkel: 'reise.kontroll.forLite', verdier: { min: sporsmal.min } };
        if (sporsmal.max !== null && tall > sporsmal.max) return { nokkel: 'reise.kontroll.forStort', verdier: { max: sporsmal.max } };
        return { verdi: tall, kundensOrd: null };
      }
      case 'tabell': {
        const svar = raderTilSvar(sporsmal.kolonner, rader);
        if (!Array.isArray(svar)) return { nokkel: 'reise.kontroll.tallICelle', verdier: { kolonne: svar.kolonne, rad: svar.rad } };
        if (sporsmal.kritisk && svar.length === 0) return { nokkel: 'reise.kontroll.minstEnRad' };
        return { verdi: svar, kundensOrd: null };
      }
      case 'fil':
        if (!fil) return { nokkel: 'reise.kontroll.velgFil' };
        return { verdi: { filId: fil.filId }, kundensOrd: null };
      case 'policy':
        if (!policy.omfang) return { nokkel: 'reise.kontroll.velgOmfang' };
        if (policy.omfang === 'noen' && !policy.kjennetegn.trim()) return { nokkel: 'reise.kontroll.beskrivKjennetegn' };
        return { verdi: policyTilSvar(policy), kundensOrd: null };
      default:
        if (!tekst.trim()) return { nokkel: 'reise.kontroll.skrivSvar' };
        return { verdi: tekst.trim(), kundensOrd: null };
    }
  }

  async function lagre(event: FormEvent) {
    event.preventDefault();
    const svar = lagSvar();
    if ('nokkel' in svar) {
      setKontroll(svar);
      return;
    }
    setKontroll(null);
    if (await onSvar({ sporsmalId: sporsmal.id, instans: sporsmal.instans, ...svar })) skrivUtkast(sporsmal, null);
  }

  async function vetIkke() {
    setKontroll(null);
    if (await onSvar({ sporsmalId: sporsmal.id, instans: sporsmal.instans, vetIkke: true })) skrivUtkast(sporsmal, null);
  }

  async function hoppOver() {
    await onSvar({ sporsmalId: sporsmal.id, instans: sporsmal.instans, hoppOver: true });
  }

  const harUtkast = endret && (typen !== 'ett_valg' || valgte.includes(ANNET));
  const melding = kontroll ? t(kontroll.nokkel, kontroll.verdier) : feil ? t(`feil.${feil}`, { defaultValue: t('feil.ukjent') }) : null;

  return (
    <form id={skjemaId} onSubmit={lagre} noValidate aria-describedby={melding ? `${skjemaId}-melding` : undefined}>
      <div className="mb-6">
        <p className="etikett mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>{t('reise.sporsmal.modul', { modul: sporsmal.modulTittel })}</span>
          {sporsmal.forelder && (
            <span className="merke bg-aksent-50 normal-case tracking-normal text-aksent-800">
              {t('reise.sporsmal.profil', { profil: sporsmal.forelder.profil })}
            </span>
          )}
        </p>
        <h2
          ref={overskrift}
          tabIndex={-1}
          id={`${skjemaId}-tekst`}
          className="max-w-lesbar text-[1.625rem] font-semibold leading-[1.25] sm:text-[1.875rem]"
        >
          {sporsmal.tekst}
        </h2>
      </div>

      {sporsmal.svar?.status === 'bekreft_pa_nytt' && <p className="mb-4 melding-advarsel">{t('reise.sporsmal.bekreftPaNytt')}</p>}
      {sporsmal.svar?.vetIkke && <p className="mb-4 melding-info">{t('reise.sporsmal.standardvalg')}</p>}

      <details className="group mb-6 max-w-lesbar">
        <summary className="inline-flex cursor-pointer select-none list-none items-center gap-2 rounded-md text-sm font-medium text-aksent-700 hover:text-aksent-900 [&::-webkit-details-marker]:hidden">
          <Info aria-hidden="true" className="h-4 w-4" />
          {t('reise.sporsmal.hvorfor')}
          <ChevronDown aria-hidden="true" className="h-4 w-4 transition-transform group-open:rotate-180" />
        </summary>
        <p className="mt-3 border-l-2 border-aksent-200 pl-4 text-slate-600">{sporsmal.hvorfor}</p>
      </details>

      <div className="max-w-lesbar">
        {!stottet && <p className="mb-4 melding-info">{t('reise.sporsmal.kommerSnart')}</p>}
        {typen === 'tabell' && <Tabellfelt id={skjemaId} kolonner={sporsmal.kolonner} rader={rader} onEndre={endre(setRader)} />}
        {typen === 'policy' && <Policyfelt id={skjemaId} policy={policy} onEndre={endre(setPolicy)} />}
        {typen === 'fil' && <Filfelt id={skjemaId} sporsmal={sporsmal} fil={fil} onEndre={endre(setFil)} />}
        {typen === 'scenario' && (
          <Profilvelger id={skjemaId} meny={sporsmal.alternativer} egne={sporsmal.annet} valgte={valgte} onEndre={endre(setValgte)} />
        )}
        {['ett_valg', 'flere_valg', 'sjekkliste'].includes(typen) && (
          <Valgfelt sporsmal={sporsmal} valgte={valgte} annet={annet} visAnbefalt={!!konsulent} onVelg={velg} onAnnet={endre(setAnnet)} />
        )}
        {typen === 'tall' && <Tallfelt sporsmal={sporsmal} tekst={tekst} onTekst={endre(setTekst)} />}
        {typen === 'tekst' && (
          <div>
            <label htmlFor={`${skjemaId}-tekst-svar`} className="sr-only">
              {t('reise.sporsmal.svar')}
            </label>
            <textarea id={`${skjemaId}-tekst-svar`} rows={5} value={tekst} onChange={(e) => endre(setTekst)(e.target.value)} className="felt w-full" />
          </div>
        )}

        {harUtkast && <p className="mt-3 text-sm text-slate-500">{t('reise.sporsmal.utkast')}</p>}

        {melding && (
          <p id={`${skjemaId}-melding`} role="alert" className="mt-4 melding-feil">
            {melding}
          </p>
        )}
      </div>

      <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-100 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          {kanGaTilbake && (
            <button type="button" onClick={onTilbake} disabled={lagrer} className="knapp-stille -ml-3 w-full sm:w-auto">
              <ArrowLeft aria-hidden="true" className="h-4 w-4" />
              {t('reise.sporsmal.tilbake')}
            </button>
          )}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {stottet && sporsmal.vetIkke && !konsulent && (
            <button type="button" onClick={vetIkke} disabled={lagrer} className="knapp-stille">
              {t('reise.sporsmal.vetIkke')}
            </button>
          )}
          {(!stottet || typen === 'fil') && !sporsmal.kritisk && !konsulent && (
            <button type="button" onClick={hoppOver} disabled={lagrer} className="knapp-sekundaer">
              {t('reise.sporsmal.hoppOver')}
            </button>
          )}
          {stottet && (
            <button type="submit" disabled={lagrer} className="knapp-primaer min-w-[12rem]">
              {lagrer ? t('reise.sporsmal.lagrer') : konsulent?.lagreTekst ?? t('reise.sporsmal.neste')}
              {!lagrer && !konsulent && <ArrowRight aria-hidden="true" className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

interface ValgfeltProps {
  sporsmal: Sporsmal;
  valgte: string[];
  annet: string;
  /** Arbeidsbordet: de ansatte ser hvilket alternativ som er anbefalt. Kunden ser det ikke. */
  visAnbefalt: boolean;
  onVelg: (verdi: string) => void;
  onAnnet: (verdi: string) => void;
}

/** Ett valg (radioknapper), flere valg og sjekklister (avkrysning), med «Annet» til slutt. */
function Valgfelt({ sporsmal, valgte, annet, visAnbefalt, onVelg, onAnnet }: ValgfeltProps) {
  const { t } = useTranslation();
  const id = `sporsmal-${sporsmal.id}`;
  const inputType = sporsmal.type === 'ett_valg' ? 'radio' : 'checkbox';
  const alternativer = [
    ...sporsmal.alternativer.map((a) => ({ verdi: a.verdi, tekst: a.tekst, anbefalt: a.anbefalt })),
    ...(sporsmal.annet ? [{ verdi: ANNET, tekst: t('reise.sporsmal.annet'), anbefalt: false }] : []),
  ];

  return (
    <fieldset aria-labelledby={`${id}-tekst`}>
      <div className="flex flex-col gap-2">
        {alternativer.map((a) => {
          const valgt = valgte.includes(a.verdi);
          return (
            <label
              key={a.verdi}
              className={'valg ' + (valgt ? 'valg-valgt' : '')}
            >
              <input
                type={inputType}
                name={`${id}-valg`}
                value={a.verdi}
                checked={valgt}
                onChange={() => onVelg(a.verdi)}
                className="h-[1.125rem] w-[1.125rem] shrink-0"
              />
              <span className="flex-1">{a.tekst}</span>
              {visAnbefalt && a.anbefalt && (
                <span className="merke bg-aksent-100 text-aksent-800">{t('reise.sporsmal.anbefalt')}</span>
              )}
            </label>
          );
        })}
      </div>
      {valgte.includes(ANNET) && (
        <div className="mt-4">
          <label htmlFor={`${id}-annet`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t('reise.sporsmal.annetTekst')}
          </label>
          <textarea id={`${id}-annet`} rows={3} value={annet} onChange={(e) => onAnnet(e.target.value)} className="felt w-full" />
        </div>
      )}
    </fieldset>
  );
}

function Tallfelt({ sporsmal, tekst, onTekst }: { sporsmal: Sporsmal; tekst: string; onTekst: (verdi: string) => void }) {
  const { t } = useTranslation();
  const id = `sporsmal-${sporsmal.id}`;
  const hjelp =
    sporsmal.min !== null && sporsmal.max !== null
      ? t('reise.sporsmal.tallMellom', { min: sporsmal.min, max: sporsmal.max })
      : sporsmal.min !== null
        ? t('reise.sporsmal.tallMinst', { min: sporsmal.min })
        : sporsmal.max !== null
          ? t('reise.sporsmal.tallHoyst', { max: sporsmal.max })
          : null;

  return (
    <div>
      <label htmlFor={`${id}-tall`} className="sr-only">
        {t('reise.sporsmal.svar')}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={`${id}-tall`}
          type="text"
          inputMode="decimal"
          value={tekst}
          onChange={(e) => onTekst(e.target.value)}
          aria-describedby={hjelp ? `${id}-hjelp` : undefined}
          className="felt w-40"
        />
        {sporsmal.enhet && <span className="text-slate-700">{sporsmal.enhet}</span>}
      </div>
      {hjelp && (
        <p id={`${id}-hjelp`} className="mt-1 text-sm text-slate-600">
          {hjelp}
        </p>
      )}
    </div>
  );
}
